import React, { useCallback, useContext, useState } from 'react'
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material'
import setNestedValue from 'lodash.set'
import { Alert, useApplicationSuiteContext } from '../../../contexts/ApplicationSuiteProvider'
import { SnackbarHelperContext } from '../../../contexts/SnackbarHelperContext'
import AlertNotificationTimeOfDayForm from '../../../components/alerts/alertForms/AlertNotificationTimeOfDayForm'
import { AlertFormSettings } from '../../../common/utils/isSymbolSelectionDisabled'
import { buildDuplicateAlert } from '../../../common/utils/buildDuplicateAlert'
import { saveNewAlert } from '../../../common/utils/saveNewAlert'
import { useAlertContext } from '../contexts/AlertContext'

// The pattern stays locked — a duplicate is the same strategy on a new
// instrument — while the symbol picker is force-unlocked even for alerts that
// carry `from: 'backtest'` attribution.
const DUPLICATE_FORM_SETTINGS: AlertFormSettings = {
  symbolDisabled: false,
  patternDisabled: true,
  dayCheckmark: false,
  allowSymbolChange: true,
}

interface DuplicateAlertDialogProps {
  open: boolean
  onClose: () => void
  sourceAlert: Alert
}

const DuplicateAlertDialog: React.FC<DuplicateAlertDialogProps> = ({
  open,
  onClose,
  sourceAlert,
}) => {
  const { canActivateAlerts, canCreateAlerts } = useApplicationSuiteContext()
  const { setSnackbarSettings } = useContext(SnackbarHelperContext)
  const { setPendingScrollAlertId } = useAlertContext()

  const [draft, setDraft] = useState<Alert>(() =>
    buildDuplicateAlert(sourceAlert, { canActivateAlerts })
  )
  const [isSaving, setIsSaving] = useState(false)

  const handleAlertKeyChange = useCallback((key: string, value: any) => {
    setDraft((prev) => setNestedValue({ ...prev }, key, value))
  }, [])

  const handleCreate = async () => {
    if (!draft.symbol || !draft.pattern) return

    // canCreateAlerts is derived from the live alerts listener, so re-reading it
    // here is a genuine re-check — the cap can be reached between opening this
    // dialog and submitting it.
    if (!canCreateAlerts) {
      setSnackbarSettings({
        severity: 'error',
        message: 'You have reached your alert limit',
        autoHideDuration: 5000,
        anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
      })
      return
    }

    setIsSaving(true)
    try {
      // `active` was resolved from canActivateAlerts when the draft was built at
      // dialog-open time. Re-clamp it here for the same reason canCreateAlerts is
      // re-read above: the active cap can be reached in between. Safe to override
      // unconditionally — the form exposes no active toggle, so this can never
      // discard a user's choice.
      const alertToCreate = canActivateAlerts ? draft : { ...draft, active: false }
      const savedAlert = await saveNewAlert(alertToCreate)
      setPendingScrollAlertId(savedAlert.id)
      setSnackbarSettings({
        severity: 'success',
        message: 'Alert duplicated!',
        autoHideDuration: 3000,
        anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
      })
      onClose()
    } catch (error) {
      console.error('Error duplicating alert:', error)
      // Deliberately leaves the dialog open with the draft intact so a transient
      // failure doesn't cost the user their symbol selection.
      setSnackbarSettings({
        severity: 'error',
        message: 'Alert failed to duplicate!',
        autoHideDuration: 5000,
        anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
      })
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Duplicate Alert</DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <AlertNotificationTimeOfDayForm
          alert={draft}
          onAlertKeyChange={handleAlertKeyChange}
          settings={DUPLICATE_FORM_SETTINGS}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          onClick={handleCreate}
          variant="contained"
          color="primary"
          disabled={!draft.symbol || isSaving}
          startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          Create Alert
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default DuplicateAlertDialog
